import { useQuery } from "@tanstack/react-query";
import QueryGlobalInterface from "interfaces/queryInterface";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import leadsWebEnviroment from "utils/leadsWebEnviroment";

export enum TipoAgendamento {
  Visita = 1,
  Assinatura = 2,
}

// Métricas base compartilhadas por totalizador, PDV e usuário.
// Campos condicionais vêm null quando não se aplicam ao tipo.
export interface IndicadorAgendamento {
  agendamentosDaSemana: number;
  percentualConversao: number;
  agendamentosParaHoje: number;
  visitasComAgendamento: number | null;
  visitasSemAgendamento: number | null;
  comparecimento: number | null;
}

export interface IndicadorAgendamentoPdv extends IndicadorAgendamento {
  idPdv: string;
  nomePdv: string;
}

export interface IndicadorAgendamentoUsuario extends IndicadorAgendamento {
  idUsuario: string;
  nomeUsuario: string;
  urlFoto?: string | null;
}

export interface IndicadoresAgendamentoPorNivel {
  pdvs: IndicadorAgendamentoPdv[];
  usuarios: IndicadorAgendamentoUsuario[];
}

export interface IndicadoresAgendamentoRequest {
  tipoAgendamento: TipoAgendamento;
  semanaAtual?: boolean;
  idPdv?: string | null;
  idUsuario?: string | null;
  enabled?: boolean;
}

const getParams = (props: IndicadoresAgendamentoRequest) => {
  const params = new URLSearchParams();

  params.append("tipoAgendamento", String(props.tipoAgendamento));
  params.append("SemanaAtual", String(props.semanaAtual ?? true));

  if (props.idPdv) params.append("idPdv", props.idPdv);
  if (props.idUsuario) params.append("idUsuario", props.idUsuario);

  return params.toString();
};

const buildOnError = (mensagem: string) => (error: any) => {
  toast?.error(
    `${mensagem} ${error?.response?.data?.notifications?.map(
      (item: any) => item?.message ?? ""
    )} `,
    { autoClose: 10000 }
  );
};

// Rota 1 — totalizador do usuário (1 objeto).
export function useIndicadoresAgendamentoTotal(
  props: IndicadoresAgendamentoRequest & QueryGlobalInterface
) {
  const fetchData = async (): Promise<IndicadorAgendamento | null> => {
    const response = await instance.get(
      `${leadsWebEnviroment}/mobile/leads/indicadores-agendamento/v1?${getParams(
        props
      )}`
    );

    return response?.data?.data ?? response?.data ?? null;
  };

  return useQuery({
    queryFn: fetchData,
    queryKey: [props.queryKey],
    refetchOnWindowFocus: false,
    enabled: props.enabled ?? true,
    onError: buildOnError("Erro ao buscar indicadores de agendamento."),
  });
}

// Rota 2 — indicadores por PDV e por usuário (mesma resposta).
export function useIndicadoresAgendamentoPorNivel(
  props: IndicadoresAgendamentoRequest & QueryGlobalInterface
) {
  const fetchData = async (): Promise<IndicadoresAgendamentoPorNivel> => {
    const response = await instance.get(
      `${leadsWebEnviroment}/mobile/leads/indicadores-agendamento/pdvs/v1?${getParams(
        props
      )}`
    );

    const payload = response?.data?.data ?? response?.data ?? {};

    return {
      pdvs: payload?.pdvs ?? [],
      usuarios: payload?.usuarios ?? [],
    };
  };

  return useQuery({
    queryFn: fetchData,
    queryKey: [props.queryKey],
    refetchOnWindowFocus: false,
    enabled: props.enabled ?? true,
    onError: buildOnError("Erro ao buscar indicadores de agendamento por PDV."),
  });
}
